/** The tour record's field catalog (plan §2–§3, D7), built from `fieldSpec.ts`.
 *
 *  Keys are the draft paths of the `tour:<id>` record — plain PATCH keys (`name`,
 *  `launch_policy`, `atag`) and the PATCH payload's dot paths (`context.<k>`,
 *  `meeting_point.<f>`, `review.<f>`, `photo_sharing.<f>`). Rows of the tour's
 *  collections carry their entity as a prefix (`alias.custom_id`, `tagcat.checkin`,
 *  `pretrip.required`), because the editor looks fields up by key alone; their catalog
 *  entries arrive with their phases (P2: aliases and tag categories, P3: pre-trip items,
 *  P4: the itinerary's days, stops and activity rows — `day.title`, `slot.time`,
 *  `activity.cost_min`).
 *
 *  `importOwned` / `importForced` (fieldSpec) become the fields' import verdicts while
 *  the tour is import-bound (`editorConfig.verdictOf`). */
import { EXT_ID_TYPES } from '@/api/extIds';
import { PRECHECK, PRECHECK_DEFAULTS } from '@/features/precheck/model';
import type { RecordChoice, RecordField } from '@/features/records/editor/types';
import { FLAG_BY_KEY } from '@/features/tag-categories/associations/flags';

import { FIELD_SPECS, spec } from '../fieldSpec';
import {
  ACTIVITY,
  DAY,
  DAY_KML,
  DAY_SLOTS,
  DAYS_PATH,
  SLOT,
  SLOT_ACTIVITIES,
  SLOT_PIN,
  TIMELINE_DISPLAY,
  TOUR_KML,
} from '../itinerary/model';

import type { TourTagcatFlag } from './collections';

export const TOUR_ENTITY = 'tour';

/** The 20 context keys, in the three groups the Content section shows. */
export const CONTEXT_GROUPS: ReadonlyArray<{ id: string; title: string; keys: readonly string[] }> = [
  { id: 'about', title: 'About', keys: ['tour_description', 'destination', 'highlights', 'inclusions', 'exclusions'] },
  {
    id: 'logistics',
    title: 'Logistics',
    keys: ['packing_items', 'notes', 'notes_link', 'local_recommendations', 'local_recommendations_link'],
  },
  {
    id: 'policies',
    title: 'Policies & commerce',
    keys: [
      'cancellation_policy',
      'cancellation_policy_link',
      'booking_terms',
      'redemption_instructions',
      'usage_instructions',
      'upsell_text',
      'upsell_link',
      'operated_by',
      'operated_by_description',
      'tour_type',
    ],
  },
];
export const CONTEXT_KEYS: readonly string[] = CONTEXT_GROUPS.flatMap((g) => g.keys);
/** Context keys holding a URL (validated; single-line input). */
export const CONTEXT_URL_KEYS: ReadonlySet<string> = new Set([
  'notes_link',
  'local_recommendations_link',
  'cancellation_policy_link',
  'upsell_link',
]);

/** The waiver context keys notifications read (Guest journey): text, a URL, and a
 *  boolean that is `true` or absent — a falsy context value deletes its key. */
export const WAIVER_CONTEXT_KEYS = ['waiver_required', 'waiver', 'waiver_link'] as const;
export const WAIVER_REQUIRED_PATH = 'context.waiver_required';

export const contextPath = (k: string) => `context.${k}`;

/** Every context path validated as a URL (the server checks the same set). */
export const CONTEXT_URL_PATHS: ReadonlySet<string> = new Set([
  ...[...CONTEXT_URL_KEYS].map(contextPath),
  contextPath('waiver_link'),
]);

/** How a known context key's stored value is edited (plan §2 S4, the non-string fix):
 *
 *  - `text`: a string (or nothing) — edited as text;
 *  - `scalar`: a number or a boolean stored under a text key (an import, an old editor)
 *    — shown as its text and edited as text; it is never sent unless edited, and an edit
 *    stores the text the operator typed;
 *  - `structured`: an object or a list — shown as JSON, read-only (it cannot be edited
 *    as text without losing its shape).
 *
 *  `waiver_required` is a boolean key of its own (on / absent). */
export type ContextValueKind = 'text' | 'scalar' | 'structured';

export function contextValueKind(v: unknown): ContextValueKind {
  if (v === null || v === undefined || typeof v === 'string') return 'text';
  if (typeof v === 'number' || typeof v === 'boolean') return 'scalar';
  return 'structured';
}

/** The draft baseline of a known text context key: text as stored, a number or boolean
 *  as its text, a structure as stored (read-only). */
export function contextBaseline(v: unknown): unknown {
  const kind = contextValueKind(v);
  if (kind === 'text') return v ?? null;
  if (kind === 'scalar') return String(v);
  return v;
}

/** `waiver_required` on (stored and truthy) or off (absent). */
export function waiverRequiredOf(v: unknown): boolean {
  if (typeof v === 'string') return v.trim() !== '' && !['false', '0', 'no'].includes(v.trim().toLowerCase());
  return Boolean(v);
}

/** Paths sent as part of one whole object (the stored object with the touched keys
 *  overlaid — `compileTourPatch` rule 3). */
export const WHOLE_OBJECT_KEYS = ['meeting_point', 'review', 'photo_sharing', 'pre_check_settings'] as const;
export type WholeObjectKey = (typeof WHOLE_OBJECT_KEYS)[number];

/** Paths holding a file token (`stagedFiles.ts`). */
export const FILE_PATHS: ReadonlySet<string> = new Set(['cover_image', 'meeting_point.photo']);

/** Paths holding an id (`{id}` on the wire) or an id set (`[{id}]`; `departures` is
 *  `[{tag: {id}}]`). */
export const ID_PATHS: ReadonlySet<string> = new Set(['launch_policy', 'vehicle_route_set']);
export const ID_SET_PATHS: ReadonlySet<string> = new Set(['atag', 'tags', 'crosssells']);
export const DEPARTURES_PATH = 'departures';

/** Where a ledger row needs its object's name ("Meeting point · Address"). */
export const OBJECT_TITLES: Readonly<Record<string, string>> = {
  meeting_point: 'Meeting point',
  review: 'Review',
  photo_sharing: 'Photo sharing',
  context: 'Content',
  pre_check_settings: 'Pre-check-in',
};

export const LOCATION_SHARING_CHOICES: RecordChoice[] = [
  { value: 0, label: 'Disabled' },
  { value: 1, label: 'Optional' },
  { value: 2, label: 'Important' },
];

/** `Group_Tour.display_currency` values (the effective one is never "operator default"). */
export const DISPLAY_CURRENCY_LABELS: Readonly<Record<number, string>> = {
  1: 'Operator default',
  2: 'Prices in USD',
  3: 'Prices in the cost currency',
  4: 'Prices in the local currency',
};

export const POSITIVE_REVIEW_TARGET_CHOICES: RecordChoice[] = [
  { value: 0, label: 'OTA if possible, URL otherwise' },
  { value: 1, label: 'OTA if possible, no review otherwise' },
  { value: 2, label: 'URL' },
  { value: 3, label: 'No review' },
];
/** `positive_review_target` value that requires `positive_review_url`. */
export const TARGET_URL = 2;

interface FieldOpts {
  widget: string | null;
  kind?: RecordField['kind'];
  label?: string;
  nullable?: boolean;
  max_length?: number;
  choices?: RecordChoice[];
  help?: string;
}

function tourField(key: string, opts: FieldOpts, entity: string = TOUR_ENTITY): RecordField {
  const s = spec(key);
  return {
    key,
    entity,
    label: opts.label ?? s.label,
    kind: opts.kind ?? 'editable',
    widget: opts.widget,
    choices: opts.choices ?? [],
    nullable: opts.nullable ?? true,
    ...(opts.max_length ? { max_length: opts.max_length } : {}),
    guards: [],
    permissions: ['console_tours_manage'],
    follows_lead: false,
    effects: [],
    automatic: [],
    restech_rule: s.importForced ? 'forced' : s.importOwned ? 'rewritten' : null,
    ...((opts.help ?? s.help) ? { help: opts.help ?? s.help } : {}),
  };
}

const text = (key: string, max_length?: number, extra: Partial<FieldOpts> = {}) =>
  tourField(key, { widget: 'text', ...(max_length ? { max_length } : {}), ...extra });
const textarea = (key: string, extra: Partial<FieldOpts> = {}) => tourField(key, { widget: 'textarea', ...extra });
const flag = (key: string) => tourField(key, { widget: 'boolean', nullable: false });

// ---------------------------------------------------------------------------
// Collection rows: aliases (`alias:<id>`) and own tag categories (`tagcat:<id>`)
// ---------------------------------------------------------------------------

export const ALIAS_ENTITY = 'alias';
export const TAGCAT_ENTITY = 'tagcat';

/** An alias's `id_type` choices, with the ext-IDs grid's labels. */
export const ID_TYPE_CHOICES: RecordChoice[] = EXT_ID_TYPES.map((t) => ({ value: t.value, label: t.label }));

const aliasField = (key: string, opts: FieldOpts) => tourField(key, opts, ALIAS_ENTITY);

export const ALIAS_FIELDS_CATALOG: RecordField[] = [
  aliasField('alias.custom_id', {
    widget: 'text',
    label: 'External ID',
    nullable: false,
    max_length: 128,
    help: 'Exactly as the booking system or the import sends it — matching compares the literal value.',
  }),
  aliasField('alias.id_type', { widget: 'enum', label: 'Type', nullable: false, choices: ID_TYPE_CHOICES }),
  aliasField('alias.use_for_matching', {
    widget: 'boolean',
    label: 'Matching',
    nullable: false,
    help: 'Off parks the ID: booking sync and the importer stop resolving products through it.',
  }),
  aliasField('alias.api_queryable', {
    widget: 'boolean',
    label: 'API',
    nullable: false,
    help: 'May be sent to the booking system as a product query. Off for matching-only keys (options, compound IDs).',
  }),
  aliasField('alias.integration', {
    widget: 'restech',
    label: 'System',
    help: 'The booking system (and account) the ID belongs to; empty = any system.',
  }),
  aliasField('alias.restech_account', { widget: 'restech', label: 'Account' }),
  aliasField('alias.comment', { widget: 'textarea', label: 'Comment', help: 'What the mapping is for, why it is parked. Never read by matching.' }),
  aliasField('alias.ext_link', { widget: 'text', label: 'External link', max_length: 2048 }),
  aliasField('alias.tour_user_tags', {
    widget: 'tags',
    label: 'Tags on match',
    help: 'Tags a participant gets when a booking matches through this ID.',
  }),
  aliasField('alias.targets', {
    widget: 'tags',
    label: 'Also books',
    help: 'Other tours a matching booking is also added to (dependent bookings).',
  }),
  // No control of its own: the identifiers table's Take over / Hand back set it.
  aliasField('alias.managed', {
    widget: null,
    label: 'Maintained by',
    nullable: false,
    help: 'An import keeps a managed ID in step on every sync, so it is read-only here until taken over; ops own the rest — no import changes or removes them.',
  }),
];

/** The tour editor's own presentation of the shared flags (`tag-categories/associations/
 *  flags.ts` owns the long labels): its column labels (`short`: the table's columns),
 *  order and details-row groups, kept as they were until the tour adopts the shared
 *  associations table. Exactly the 18 booleans the tour editor edits (`TAGCAT_BOOLEANS`)
 *  — never `departure_key`. */
const TOUR_TAGCAT_FLAGS: ReadonlyArray<{ flag: TourTagcatFlag; short?: string; group: string }> = [
  { flag: 'checkin', short: 'Check-in', group: 'Check-in & boarding' },
  { flag: 'boarding', short: 'Boarding', group: 'Check-in & boarding' },
  { flag: 'checkin_display', group: 'Check-in & boarding' },
  { flag: 'chat_conversation', short: 'Chat', group: 'Chat' },
  { flag: 'visible_in_chat', group: 'Chat' },
  { flag: 'user_selected', short: 'Self-select', group: 'Guests' },
  { flag: 'visible_on_bookingmgmt', group: 'Guests' },
  { flag: 'location_sharing', short: 'Locations', group: 'Location' },
  { flag: 'location_all_if_no_tag', group: 'Location' },
  { flag: 'user_association', group: 'Assignment' },
  { flag: 'guides_from_tags', short: 'Guides', group: 'Assignment' },
  { flag: 'copy_tags_to_guides', group: 'Assignment' },
  { flag: 'copy_tags_to_subbookings', group: 'Assignment' },
  { flag: 'cleanup_on_assignment', group: 'Assignment' },
  { flag: 'remove_guides_on_cleanup', group: 'Assignment' },
  { flag: 'remove_unreferenced_guides', group: 'Assignment' },
  { flag: 'departure_wide', group: 'Assignment' },
  { flag: 'remove_upon_date_changes', group: 'Assignment' },
];

/** The tag-category flags with their labels, grouped as the details row shows them; the
 *  first ones are the table's columns. */
export const TAGCAT_FLAG_LABELS: ReadonlyArray<{ flag: TourTagcatFlag; label: string; short?: string; group: string }> =
  TOUR_TAGCAT_FLAGS.map((f) => ({ ...f, label: FLAG_BY_KEY[f.flag].label }));

const tagcatField = (key: string, opts: FieldOpts) => tourField(key, opts, TAGCAT_ENTITY);

export const TAGCAT_FIELDS_CATALOG: RecordField[] = [
  // Chosen when the row is added; a stored row's category never changes (the funnel
  // ignores it) — remove the row and add another instead.
  tagcatField('tagcat.category', { widget: null, label: 'Category', nullable: false }),
  tagcatField('tagcat.min_tags', { widget: 'integer', label: 'Min tags', nullable: false, help: 'At least this many tags per guest (0 = optional).' }),
  tagcatField('tagcat.max_tags', { widget: 'integer', label: 'Max tags', nullable: false, help: 'At most this many tags per guest (0 = unlimited).' }),
  ...TAGCAT_FLAG_LABELS.map((f) => tagcatField(`tagcat.${f.flag}`, { widget: 'boolean', label: f.short ?? f.label, nullable: false })),
];

// ---------------------------------------------------------------------------
// Pre-trip items (`pretrip:<id>`)
// ---------------------------------------------------------------------------

export const PRETRIP_ENTITY = 'pretrip';

const pretripField = (key: string, opts: FieldOpts) => tourField(key, opts, PRETRIP_ENTITY);

export const PRETRIP_FIELDS_CATALOG: RecordField[] = [
  // Chosen when the row is added (from the catalog or another tour); a row's item does
  // not change — remove it and add another.
  pretripField('pretrip.item', { widget: null, label: 'Item', nullable: false }),
  pretripField('pretrip.required', { widget: 'boolean', label: 'Required', nullable: false, help: 'Guests must check it off.' }),
  pretripField('pretrip.all_users', { widget: 'boolean', label: 'All users', nullable: false, help: 'Everyone on the tour, staff included.' }),
  pretripField('pretrip.all_travelers', { widget: 'boolean', label: 'All travelers', nullable: false, help: 'Every booked guest without a staff role.' }),
  pretripField('pretrip.roles', { widget: 'tags', label: 'Roles', help: 'Staff roles (legacy targeting).' }),
  pretripField('pretrip.agroups', { widget: 'tags', label: 'Access groups' }),
  pretripField('pretrip.users', { widget: 'tags', label: 'Users', help: 'Individual users booked on the tour (fixed-date tours).' }),
];

// ---------------------------------------------------------------------------
// The itinerary (`day:<id>`, `slot:<id>`, `activity:<id>` — P4, `itinerary/model.ts`)
// ---------------------------------------------------------------------------

const dayField = (key: string, opts: FieldOpts) => tourField(key, opts, DAY);
const slotField = (key: string, opts: FieldOpts) => tourField(key, opts, SLOT);
const activityField = (key: string, opts: FieldOpts) => tourField(key, opts, ACTIVITY);

export const TIMELINE_DISPLAY_CHOICES: RecordChoice[] = TIMELINE_DISPLAY.map((o) => ({ value: o.value, label: o.label }));

export const DAY_FIELDS_CATALOG: RecordField[] = [
  dayField('day.title', { widget: 'text', label: 'Title', max_length: 256 }),
  dayField('day.subtitle', { widget: 'text', label: 'Subtitle', max_length: 256 }),
  dayField('day.description', { widget: 'richtext', label: 'Description' }),
  dayField('day.day_span', { widget: 'integer', label: 'Days', nullable: false, help: 'How many calendar days this itinerary day covers.' }),
  dayField(DAY_SLOTS, { widget: null, kind: 'derived', label: 'Stops' }),
  dayField(DAY_KML, { widget: 'kml', label: 'Day route (KML)', help: 'The route the app draws on the map for this day.' }),
];

export const SLOT_FIELDS_CATALOG: RecordField[] = [
  slotField('slot.time', { widget: 'itin_time', label: 'Time', nullable: false, help: 'The stops of a day are listed in time order.' }),
  slotField('slot.title', { widget: 'text', label: 'Title', max_length: 256 }),
  slotField('slot.subtitle', { widget: 'text', label: 'Subtitle', max_length: 256 }),
  slotField('slot.description', { widget: 'richtext', label: 'Description' }),
  slotField('slot.timeline_display', { widget: 'enum', label: 'Timeline', nullable: false, choices: TIMELINE_DISPLAY_CHOICES, help: 'What the timeline shows beside the stop: its time, a label, or nothing.' }),
  slotField('slot.timeline_text', { widget: 'text', label: 'Timeline label', max_length: 256 }),
  slotField('slot.show_transport', { widget: 'boolean', label: 'Show transport', nullable: false }),
  slotField('slot.next_transport', { widget: 'transport_picker', label: 'Transport type' }),
  slotField('slot.transport_text', { widget: 'text', label: 'Transport text', max_length: 128 }),
  slotField(SLOT_ACTIVITIES, { widget: null, kind: 'derived', label: 'Activities' }),
  slotField(SLOT_PIN, { widget: 'pin', label: 'Map pin', help: 'Numbered (in order, or a number of your own) or one of the 12 standard icons. Applied after the tour is saved.' }),
];

export const ACTIVITY_FIELDS_CATALOG: RecordField[] = [
  activityField('activity.activity', { widget: 'activity_picker', label: 'Activity', nullable: false }),
  activityField('activity.cost_prefix', { widget: 'text', label: 'Price prefix', max_length: 64 }),
  activityField('activity.cost_min', { widget: 'decimal', label: 'Min price' }),
  activityField('activity.cost_max', { widget: 'decimal', label: 'Max price', help: 'Only for a price range.' }),
  activityField('activity.cost_unit', { widget: 'text', label: 'Unit', max_length: 64 }),
  activityField('activity.currency', { widget: 'currency_picker', label: 'Currency' }),
  activityField('activity.display_name', { widget: 'text', label: 'Display name', max_length: 256, help: 'Shown instead of the activity’s own name.' }),
  activityField('activity.display_on_map', { widget: 'boolean', label: 'Show on map', nullable: false }),
];

export const ITINERARY_FIELDS_CATALOG: RecordField[] = [
  tourField(DAYS_PATH, { widget: null, kind: 'derived', label: 'Itinerary days' }),
  tourField(TOUR_KML, { widget: 'kml', label: 'Tour route (KML)', help: 'The route the app draws for the whole tour (a day’s own route wins on that day).' }),
  ...DAY_FIELDS_CATALOG,
  ...SLOT_FIELDS_CATALOG,
  ...ACTIVITY_FIELDS_CATALOG,
];

// ---------------------------------------------------------------------------
// Pre-check-in (`pre_check_settings.<k>` — the tour's own settings, sent whole)
// ---------------------------------------------------------------------------

// `PRECHECK` (the path) and `PRECHECK_DEFAULTS` live in the shared pre-check model.
export { PRECHECK, PRECHECK_DEFAULTS };

/** The help of a field list: a field is the operator's (`Pre_Check_In_Field`), so its name
 *  is shared by every tour that asks it. */
const SHARED_FIELD_HELP = 'A field belongs to the operator, shared by all its tours: renaming one here renames it on every tour';

/** The pre-check-in settings' fields — one set of labels and help for both editors (the
 *  wording rule, backend plan 2026-10-08): the tour's own settings (`tour:<id>`) and the
 *  operator's (`tour_operator:<id>`, its Pre-check-in tab). Only what is the tour's alone
 *  differs: a tour's list keeps its own required flags and order. */
export function precheckFieldsCatalog(entity: string = TOUR_ENTITY, permission = 'console_tours_manage'): RecordField[] {
  const field = (k: string, opts: FieldOpts) => ({ ...tourField(PRECHECK(k), opts, entity), permissions: [permission] });
  const flag = (k: string, label: string, help?: string) =>
    field(k, { widget: 'boolean', nullable: false, label, ...(help ? { help } : {}) });
  const listHelp =
    entity === TOUR_ENTITY ? `${SHARED_FIELD_HELP} (required and order are this tour’s own).` : `${SHARED_FIELD_HELP}.`;
  return [
    flag('enabled', 'Pre-check-in flow active', 'Guests are asked to pre-check in before the trip.'),
    flag('required', 'Guests must complete it'),
    flag('allow_edit_after_checkin', 'Guests may edit after pre-check-in'),
    field('intro_text', { widget: 'textarea', label: 'Intro text' }),
    field('confirmation_message', {
      widget: 'textarea',
      label: 'Confirmation message',
      help: 'Supports {{ template.variables }}.',
    }),
    field('lead_booker_fields', { widget: 'precheck_fields', label: 'Lead booker fields', help: listHelp }),
    flag('lead_tag_categories_enabled', 'Lead booker: questions from tag categories', 'The lead booker picks tags of these categories.'),
    field('lead_selected_tag_categories', { widget: 'precheck_categories', label: 'Lead booker: question categories' }),
    field('apply_same_fields_to_party_members', {
      widget: 'precheck_apply_same',
      nullable: false,
      label: 'Same fields for party members',
      help: 'Party members answer the lead booker’s fields and questions — copied whenever those change.',
    }),
    flag('collect_party_members_info', 'Collect party members’ information'),
    flag('include_party_members_in_chat', 'Party members join the chat'),
    field('party_member_fields', { widget: 'precheck_fields', label: 'Party member fields', help: listHelp }),
    flag('party_tag_categories_enabled', 'Party members: questions from tag categories', 'Party members pick tags of these categories.'),
    field('party_selected_tag_categories', { widget: 'precheck_categories', label: 'Party members: question categories' }),
    flag('waiver_enabled', 'Pre-check-in has a waiver'),
    field('waivers', { widget: 'precheck_waivers', label: 'Waivers' }),
  ];
}

export const PRECHECK_FIELDS_CATALOG: RecordField[] = precheckFieldsCatalog();

/** Every field the tour record edits in this phase. */
export const TOUR_FIELDS: RecordField[] = [
  // ---- Launch & mapping
  tourField('custom_id', { widget: 'text', label: 'Primary external ID', max_length: 128 }),
  tourField('additional_custom_ids.order', { widget: null, kind: 'derived', label: 'External IDs' }),
  ...ALIAS_FIELDS_CATALOG,
  tourField('launch_policy', { widget: 'launch_policy' }),
  tourField('atag', { widget: 'tags' }),
  tourField('tags', { widget: 'tags' }),
  tourField('tag_categories.order', { widget: null, kind: 'derived', label: 'Tag categories' }),
  ...TAGCAT_FIELDS_CATALOG,

  // ---- Basics
  text('name', 1024, { nullable: false }),
  text('internal_name', 1024),
  text('description', 80),
  tourField('start_timezone', { widget: 'timezone', nullable: false }),
  flag('use_as_template'),
  tourField('cover_image', { widget: 'image' }),
  text('meeting_point.name', 512),
  text('meeting_point.address', 1024),
  tourField('meeting_point.latitude', { widget: 'coordinates', label: 'Coordinates', help: 'Latitude and longitude of the meeting point.' }),
  tourField('meeting_point.longitude', { widget: 'coordinates', label: 'Longitude' }),
  text('meeting_point.maps_url'),
  textarea('meeting_point.comment', { label: 'Instructions' }),
  tourField('meeting_point.photo', { widget: 'image' }),
  tourField('location_sharing', { widget: 'enum', nullable: false, choices: LOCATION_SHARING_CHOICES }),
  flag('mytrips_display'),
  flag('has_group_chat'),
  flag('exclude_from_chat'),
  flag('disable_internal_chat'),
  tourField('show_all_users_to_guides_in_nonbespoke_tours', { widget: 'boolean' }),
  flag('use_operator_defaults'),
  tourField('chat_priority', { widget: 'integer', kind: 'derived' }),
  tourField('effective_display_currency', { widget: null, kind: 'derived' }),

  // ---- Schedule & departures
  tourField('start_date', { widget: 'date', nullable: false }),
  tourField('end_date', { widget: null, kind: 'derived' }),
  tourField('departures', { widget: 'tags' }),
  tourField('length', { widget: 'length' }),
  tourField('length_is_manual', { widget: 'boolean', nullable: false }),

  // ---- Content
  ...CONTEXT_KEYS.map((k) =>
    CONTEXT_URL_KEYS.has(k) ? text(contextPath(k)) : tourField(contextPath(k), { widget: 'text_lazy' }),
  ),
  text('review.name'),
  text('review.url'),
  text('photo_sharing.name'),
  text('photo_sharing.url'),
  tourField('positive_review_target', { widget: 'enum', nullable: false, choices: POSITIVE_REVIEW_TARGET_CHOICES }),
  text('positive_review_url'),
  tourField('crosssells', { widget: 'tags' }),

  // ---- Guest journey
  flag('allow_selfreg'),
  tourField('selfreg_verify_email', { widget: 'boolean' }),
  tourField('selfreg_verify_phone', { widget: 'boolean' }),
  flag('use_operator_pre_check_settings'),
  ...PRECHECK_FIELDS_CATALOG,
  flag(WAIVER_REQUIRED_PATH),
  tourField(contextPath('waiver'), { widget: 'text_lazy' }),
  text(contextPath('waiver_link')),
  tourField('pre_trip_items.order', { widget: null, kind: 'derived', label: 'Pre-trip items' }),
  ...PRETRIP_FIELDS_CATALOG,

  // ---- Itinerary tab (bus trips)
  tourField('vehicle_route_set', { widget: 'route_set' }),

  // ---- Itinerary tab (tours): the workspace
  ...ITINERARY_FIELDS_CATALOG,
];

export const TOUR_FIELD_BY_KEY: ReadonlyMap<string, RecordField> = new Map(TOUR_FIELDS.map((f) => [f.key, f]));

/** fieldSpec's import flags by catalog key. */
export function importRule(key: string): 'forced' | 'owned' | null {
  const s = FIELD_SPECS[key];
  if (s?.importForced) return 'forced';
  if (s?.importOwned) return 'owned';
  return null;
}

/** Fields whose change re-syncs the tour's chat channels — the inputs of
 *  `Group_Tour.get_tour_chat_hash` the editor can change (the PATCH compares the hash
 *  before and after). */
export const CHAT_KEYS: ReadonlySet<string> = new Set([
  'name',
  'has_group_chat',
  'exclude_from_chat',
  'disable_internal_chat',
  'cover_image',
  'start_timezone',
  'use_operator_defaults',
]);
